import React, { useContext, useEffect, useState } from 'react';
import { FooterProps, HeaderProps } from '@spotify-internal/masthead-react';
import { useProperty } from '@spotify-internal/remote-config-integration-react';

import { getMasthead } from 'utils/masthead/masthead';
import { MastheadData as TurnkeyMastheadData, MastheadFooterData, MastheadHeaderData } from 'lib/sanity/types';
import { useTurnkeyMastheadInConsumerBool } from 'utils/remote-config/properties';
import englishSpeakingMarkets from 'utils/remote-config/english-speaking-markets';

import { useServerSideConstants } from './ServerSideConstantsProvider';

interface MastheadProviderProps {
  turnkeyMastheadData?: TurnkeyMastheadData;
  children: React.ReactNode;
}

interface MastheadData {
  header?: HeaderProps;
  footer?: FooterProps;
  turnkeyHeader?: MastheadHeaderData;
  turnkeyFooter?: MastheadFooterData;
  useTurnkeyMasthead?: boolean | null;
}

const MastheadContext = React.createContext<MastheadData>({});

export function useMastheadData() {
  return useContext(MastheadContext);
}

const MastheadProvider: React.FC<MastheadProviderProps> = ({ children, turnkeyMastheadData }) => {
  const turnkeyMastheadInConsumerBool = useProperty(useTurnkeyMastheadInConsumerBool, { loadingValue: null });
  const {
    isPlain,
    market: { marketCode },
    verticalConfig: {
      features: { configureMasthead },
    },
  } = useServerSideConstants();

  const englishMarket = englishSpeakingMarkets.includes(marketCode);
  const useTurnkeyMasthead = englishMarket && turnkeyMastheadInConsumerBool === true;
  const useSharedMasthead = !englishMarket || turnkeyMastheadInConsumerBool === false;

  const [header, setHeader] = useState<HeaderProps>();
  const [footer, setFooter] = useState<FooterProps>();

  useEffect(() => {
    const getMastheadData = async () => {
      const res = await getMasthead(marketCode);
      setFooter(res.footer);
      setHeader(res.header);
    };
    if (!configureMasthead && !isPlain && useSharedMasthead) {
      getMastheadData();
    }
  }, [marketCode, isPlain, configureMasthead, useSharedMasthead]);

  return (
    <MastheadContext.Provider
      value={{
        header,
        footer,
        turnkeyHeader: turnkeyMastheadData?.header,
        turnkeyFooter: turnkeyMastheadData?.footer,
        useTurnkeyMasthead,
      }}
    >
      {children}
    </MastheadContext.Provider>
  );
};

export default MastheadProvider;
